/**
 * Crew location sharing.
 *
 * LocationSharing does the work — start(): POST sharing on → navigator.geolocation.watchPosition → post fixes.
 * Throttle: never more than one post per 3 s; between those, a fix is sent when the device moved
 * > 5 m since the last sent fix, or as a 10 s heartbeat so crew freshness stays "live".
 * Offline / network errors queue fixes (capped) and flush them through the batch endpoint when
 * the connection returns. stop(): clear the watch, flush, POST sharing off.
 *
 * Facing (round 4): while the phone is likely aimed (compass running, reading < 3 s old, not lying
 * flat — lib/compass aimedFacing) each post carries facingDeg / facingAccuracyDeg / facingT, so a
 * manned camera's cone follows where its operator points. Turning ≥ 10° re-sends the last fix with
 * the new facing (still ≤ 1 post per 3 s), and an aimed phone posts at least every 5 s so the aim
 * never goes stale (> 10 s) while it is held up.
 *
 * AutoShare decides WHEN: sharing runs by itself while the open mission is live and the
 * "Share my location during live missions" setting (prefs.shareLocation, default on) is on, as
 * long as the phone allows location. It asks the phone at most once, never re-prompts after a
 * "no", stops when the mission ends or the setting goes off, and shares one mission at a time.
 */
import { useSyncExternalStore } from 'react';
import type { EpochMs, Id } from '@apos/shared';
import { api, ApiError, type CrewLocationInput } from './api';
import { serverNow } from './clock';
import { getNative, onNative, type NativeMissionState, type NativePermission, type SightlineNative } from './native';
import { aimedFacing, compass } from './compass';

/** A facing to send: degrees true, ± accuracy, and when it was read (device ms). */
export interface Facing {
  deg: number;
  accuracyDeg: number | null;
  t: number;
}

/** Re-send the last fix when the phone turned this much (°) since the last sent facing. */
export const FACING_RESEND_DEG = 10;
/** An aimed phone posts at least this often (ms) — under the 10 s "aim unknown" threshold. */
export const FACING_HEARTBEAT_MS = 5000;
/** Only re-send a held fix with a new facing while the fix is this fresh (ms). */
const FACING_FIX_MAX_AGE_MS = 15_000;

const angleGap = (a: number, b: number) => {
  const d = Math.abs((((a - b) % 360) + 360) % 360);
  return d > 180 ? 360 - d : d;
};

export type SharingStatus = 'off' | 'requesting' | 'active' | 'denied' | 'unsupported' | 'error';

export interface Fix {
  t: EpochMs;
  lat: number;
  lon: number;
  accuracyM: number | null;
  altM: number | null;
  speedMps: number | null;
  headingDeg: number | null;
}

export interface SharingState {
  status: SharingStatus;
  missionId: Id | null;
  lastFix: Fix | null;
  lastSentAt: EpochMs | null;
  queued: number;
  error: string | null;
}

export interface ThrottleOptions {
  minIntervalMs: number;
  minMoveM: number;
  heartbeatMs: number;
}

export const DEFAULT_THROTTLE: ThrottleOptions = { minIntervalMs: 3000, minMoveM: 5, heartbeatMs: 10_000 };

/** Haversine metres (local; avoids a dependency on the shared geo lib for a hot path). */
export function quickDistanceM(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const R = 6_371_008.8;
  const toR = Math.PI / 180;
  const dLat = (b.lat - a.lat) * toR;
  const dLon = (b.lon - a.lon) * toR;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * toR) * Math.cos(b.lat * toR) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** Pure throttle decision. `last` is the last fix that was sent (or queued). */
export function shouldSend(last: Pick<Fix, 't' | 'lat' | 'lon'> | null, next: Pick<Fix, 't' | 'lat' | 'lon'>, o: ThrottleOptions = DEFAULT_THROTTLE): boolean {
  if (!last) return true;
  const dt = next.t - last.t;
  if (dt < o.minIntervalMs) return false;
  if (dt >= o.heartbeatMs) return true;
  return quickDistanceM(last, next) > o.minMoveM;
}

const QUEUE_CAP = 3000;
/** A fix rejected as "before the mission started" is re-sent this much later, re-stamped. */
const RESTAMP_DELAY_MS = 1500;
const BATCH_SIZE = 200;

type Listener = () => void;

export class LocationSharing {
  private state: SharingState = { status: 'off', missionId: null, lastFix: null, lastSentAt: null, queued: 0, error: null };
  private listeners = new Set<Listener>();
  private watchId: number | null = null;
  /** Phones (iOS especially) rarely fire watchPosition while standing still; poll for a fresh fix. */
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private lastSent: Fix | null = null;
  private queue: CrewLocationInput[] = [];
  private flushing = false;
  private throttle: ThrottleOptions;
  private geo: Geolocation | null;
  private facing: () => Facing | null;
  private lastSentFacing: number | null = null;
  /** The re-stamped retry already made for a rejected-as-too-early point (one retry per fix). */
  private restamped: number | null = null;

  constructor(
    opts: {
      throttle?: ThrottleOptions;
      geolocation?: Geolocation | null;
      /** Where the phone points while aimed (default: the compass, see aimedFacing). */
      facing?: () => Facing | null;
      /** Called on every facing change (default: compass updates). */
      subscribeFacing?: (fn: () => void) => () => void;
    } = {},
  ) {
    this.throttle = opts.throttle ?? DEFAULT_THROTTLE;
    this.geo = opts.geolocation !== undefined ? opts.geolocation : typeof navigator !== 'undefined' ? navigator.geolocation ?? null : null;
    this.facing = opts.facing ?? (() => aimedFacing(compass.getState()));
    (opts.subscribeFacing ?? compass.subscribe)(() => this.onFacing());
    if (typeof window !== 'undefined') window.addEventListener('online', () => void this.flush());
  }

  get snapshot(): SharingState {
    return this.state;
  }

  subscribe = (fn: Listener) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };
  getSnapshot = () => this.state;

  async start(missionId: Id): Promise<void> {
    if (!this.geo) {
      this.set({ status: 'unsupported', missionId, error: 'This browser cannot share location.' });
      return;
    }
    if (this.state.status === 'active' && this.state.missionId === missionId) return;
    if (this.state.missionId && this.state.missionId !== missionId) await this.stop();
    this.set({ status: 'requesting', missionId, error: null });
    try {
      await api.setSharing(missionId, true);
    } catch (e) {
      this.set({ status: 'error', error: e instanceof Error ? e.message : String(e) });
      return;
    }
    this.watchId = this.geo.watchPosition(
      (pos) => this.onPosition(pos),
      (err) => this.onGeoError(err),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 20_000 },
    );
    this.startRefresh();
  }

  private startRefresh(): void {
    this.stopRefresh();
    const geo = this.geo;
    if (!geo) return;
    this.refreshTimer = setInterval(() => {
      if (this.state.status !== 'active' && this.state.status !== 'requesting') return;
      const last = this.state.lastFix;
      if (last && Date.now() - last.t < this.throttle.heartbeatMs - 500) return; // the watch is delivering
      geo.getCurrentPosition(
        // This poll IS the heartbeat: it fires ~10 s after the last fix, a hair under the
        // heartbeat threshold, and waiting for the next poll would leave 20 s gaps (crew "live"
        // lasts 15 s) — so send it (still at most one post per minIntervalMs).
        (pos) => this.onPosition(pos, true),
        () => undefined, // the watch reports errors
        { enableHighAccuracy: true, maximumAge: 5_000, timeout: 15_000 },
      );
    }, this.throttle.heartbeatMs);
  }

  private stopRefresh(): void {
    if (this.refreshTimer !== null) clearInterval(this.refreshTimer);
    this.refreshTimer = null;
  }

  async stop(): Promise<void> {
    const missionId = this.state.missionId;
    if (this.watchId !== null && this.geo) this.geo.clearWatch(this.watchId);
    this.watchId = null;
    this.stopRefresh();
    if (missionId) {
      await this.flush();
      try {
        await api.setSharing(missionId, false);
      } catch {
        /* server forces sharing off at mission end anyway */
      }
    }
    this.lastSent = null;
    this.lastSentFacing = null;
    this.set({ status: 'off', missionId: null, error: null });
  }

  /**
   * Record that location is blocked for `missionId` without asking the device again (the phone
   * or browser already said no). The UI shows a small "How to fix" hint.
   */
  block(missionId: Id, status: 'denied' | 'unsupported' = 'denied', error: string | null = null): void {
    this.set({ status, missionId, error: error ?? (status === 'denied' ? 'Location is blocked for Airview Studio.' : 'This device cannot share location.') });
  }

  /** Exposed for tests: feed a fix as if it came from the device. */
  ingest(fix: Fix, heartbeat = false): void {
    this.set({ lastFix: fix, status: this.state.status === 'requesting' ? 'active' : this.state.status });
    if (!this.state.missionId) return;
    const facing = this.facing();
    const due = heartbeat && (!this.lastSent || fix.t - this.lastSent.t >= this.throttle.minIntervalMs);
    if (!due && !shouldSend(this.lastSent, fix, this.throttle) && !this.facingDue(fix.t, facing)) return;
    this.post(fix, facing);
  }

  /** The aimed facing turned or is about to go stale: worth a post of its own (≥ minIntervalMs apart). */
  private facingDue(t: EpochMs, f: Facing | null): boolean {
    if (!f || !this.lastSent || t - this.lastSent.t < this.throttle.minIntervalMs) return false;
    if (this.lastSentFacing == null || t - this.lastSent.t >= FACING_HEARTBEAT_MS) return true;
    return angleGap(f.deg, this.lastSentFacing) >= FACING_RESEND_DEG;
  }

  /** Compass update: re-send the (fresh) last fix with the new facing when it is due. */
  private onFacing(): void {
    const fix = this.state.lastFix;
    if (!fix || !this.state.missionId || this.state.status !== 'active') return;
    const now = Math.round(serverNow());
    if (now - fix.t > FACING_FIX_MAX_AGE_MS) return;
    const f = this.facing();
    if (!this.facingDue(now, f)) return;
    this.post({ ...fix, t: now }, f);
  }

  private post(fix: Fix, facing: Facing | null): void {
    if (getNative()?.cameraPerspectiveActive === true) return;
    this.lastSent = fix;
    this.lastSentFacing = facing ? facing.deg : null;
    const point: CrewLocationInput = {
      lat: fix.lat,
      lon: fix.lon,
      accuracyM: fix.accuracyM,
      altM: fix.altM,
      speedMps: fix.speedMps,
      headingDeg: fix.headingDeg,
      t: fix.t,
      source: getNative() ? 'ios' : 'browser',
    };
    if (facing) {
      // the compass clock is the device's; stamp it in server time like the fix (never after the fix)
      point.facingDeg = Math.round(facing.deg * 10) / 10;
      point.facingAccuracyDeg = facing.accuracyDeg;
      point.facingT = Math.min(fix.t, Math.round(serverNow() - (Date.now() - facing.t)));
    }
    void this.send(point);
  }

  private onPosition(pos: GeolocationPosition, heartbeat = false): void {
    const c = pos.coords;
    // Stamp in server time so the mission timeline is consistent across devices.
    const fix: Fix = {
      t: Math.round(serverNow()),
      lat: c.latitude,
      lon: c.longitude,
      accuracyM: Number.isFinite(c.accuracy) ? c.accuracy : null,
      altM: c.altitude ?? null,
      speedMps: c.speed ?? null,
      headingDeg: c.heading !== null && Number.isFinite(c.heading) ? c.heading : null,
    };
    this.ingest(fix, heartbeat);
  }

  private onGeoError(err: GeolocationPositionError): void {
    if (err.code === err.PERMISSION_DENIED) {
      const missionId = this.state.missionId;
      if (this.watchId !== null && this.geo) this.geo.clearWatch(this.watchId);
      this.watchId = null;
      this.stopRefresh();
      this.set({ status: 'denied', error: getNative() ? 'Location is off for Airview Studio in iOS Settings.' : 'Location permission denied. Enable it in browser settings to share.' });
      if (missionId) void api.setSharing(missionId, false).catch(() => undefined);
    } else {
      // TIMEOUT / POSITION_UNAVAILABLE: keep watching; surface the problem.
      this.set({ error: err.code === err.TIMEOUT ? 'Waiting for a GPS fix…' : 'Position unavailable.' });
    }
  }

  private async send(point: CrewLocationInput): Promise<void> {
    if (getNative()?.cameraPerspectiveActive === true) return;
    const missionId = this.state.missionId;
    if (!missionId) return;
    if (this.queue.length > 0 || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
      this.enqueue(point);
      void this.flush();
      return;
    }
    try {
      await api.postCrewLocation(missionId, point);
      this.set({ lastSentAt: point.t ?? null, error: this.state.status === 'active' ? null : this.state.error, status: 'active' });
    } catch (e) {
      this.handleSendError(e, point);
    }
  }

  private handleSendError(e: unknown, point?: CrewLocationInput): void {
    if (e instanceof ApiError && !e.isNetwork) {
      if (e.status === 403 || e.status === 409) {
        // not sharing any more / mission not live: stop the watch, keep state honest
        if (this.watchId !== null && this.geo) this.geo.clearWatch(this.watchId);
        this.watchId = null;
        this.stopRefresh();
        this.queue = [];
        this.set({ status: 'error', queued: 0, error: e.message || 'Server refused location (mission not live?).' });
        return;
      }
      if (e.code === 'time_out_of_range' && point?.t != null && this.restamped !== point.t) {
        // stamped a moment before the mission started (the first fix as a shoot goes live, or
        // a clock estimate still settling): the position is still current — send it once more,
        // re-stamped, instead of leaving the crew member off the map until the next heartbeat
        const missionId = this.state.missionId;
        const retry = { ...point, t: point.t + RESTAMP_DELAY_MS };
        this.restamped = retry.t;
        setTimeout(() => {
          if (this.state.missionId === missionId && this.state.status === 'active') void this.send({ ...retry, t: Math.max(retry.t, Math.round(serverNow())) });
        }, RESTAMP_DELAY_MS);
      }
      this.set({ error: e.message });
      return;
    }
    if (point) this.enqueue(point);
  }

  private enqueue(point: CrewLocationInput): void {
    this.queue.push(point);
    if (this.queue.length > QUEUE_CAP) this.queue.splice(0, this.queue.length - QUEUE_CAP);
    this.set({ queued: this.queue.length });
  }

  /** Flush queued points via the batch endpoint. */
  async flush(): Promise<void> {
    const missionId = this.state.missionId;
    if (!missionId || this.flushing || this.queue.length === 0 || getNative()?.cameraPerspectiveActive === true) return;
    this.flushing = true;
    try {
      while (this.queue.length > 0 && getNative()?.cameraPerspectiveActive !== true) {
        const chunk = this.queue.slice(0, BATCH_SIZE);
        await api.postCrewLocationBatch(missionId, chunk);
        this.queue.splice(0, chunk.length);
        this.set({ queued: this.queue.length, lastSentAt: chunk[chunk.length - 1]?.t ?? this.state.lastSentAt });
      }
    } catch (e) {
      this.handleSendError(e);
    } finally {
      this.flushing = false;
    }
  }

  private set(p: Partial<SharingState>): void {
    this.state = { ...this.state, ...p };
    for (const l of this.listeners) l();
  }
}

export const locationSharing = new LocationSharing();

export function useLocationSharing(): SharingState {
  return useSyncExternalStore(locationSharing.subscribe, locationSharing.getSnapshot, locationSharing.getSnapshot);
}

/** Above this horizontal accuracy a phone fix is not used as the pointing reference. */
export const REFERENCE_MAX_ACCURACY_M = 150;

/** "±8 m" / "±3.6 km". */
export function formatAccuracy(accuracyM: number): string {
  return accuracyM >= 1000 ? `±${(accuracyM / 1000).toFixed(1)} km` : `±${Math.round(accuracyM)} m`;
}

/** Where to turn on precise location: the Airview Studio app has its own Settings page, Safari does not. */
export const NATIVE_LOCATION_PATH = 'Settings › Airview Studio › Location → While Using + Precise Location on';
export const SAFARI_LOCATION_PATH = 'Settings › Privacy & Security › Location Services › Safari Websites → While Using + Precise Location on';

/** Human hint when a fix is too coarse to point from (iOS "Precise Location" is the usual cause). */
export function accuracyAdvice(accuracyM: number | null, native: boolean = getNative() !== null): string | null {
  if (accuracyM == null || accuracyM <= REFERENCE_MAX_ACCURACY_M) return null;
  return `GPS only ${formatAccuracy(accuracyM)}. On iPhone: ${native ? NATIVE_LOCATION_PATH : SAFARI_LOCATION_PATH}. Go outside for a clear sky view.`;
}

// ───────────── automatic sharing ─────────────

/** Location permission as far as the web page can tell ('unknown' = no Permissions API). */
export type LocationPermission = NativePermission | 'unknown';

/** The parts of LocationSharing that AutoShare drives (a fake in tests). */
export interface SharingControl {
  readonly snapshot: SharingState;
  subscribe(fn: () => void): () => void;
  start(missionId: Id): Promise<void>;
  stop(): Promise<void>;
  block(missionId: Id, status?: 'denied' | 'unsupported', error?: string | null): void;
}

export interface AutoShareDeps {
  sharing: SharingControl;
  /** The iOS app bridge, when running inside it. */
  native?: () => SightlineNative | null;
  /** Browser permission state (Permissions API); called only outside the app. */
  queryPermission?: (onChange: (p: LocationPermission) => void) => Promise<LocationPermission>;
  /** After a server/network failure, try again this often (ms). */
  retryMs?: number;
}

/** navigator.permissions.query({name:'geolocation'}), subscribing to changes once. */
function browserPermissionQuery(): NonNullable<AutoShareDeps['queryPermission']> {
  let status: PermissionStatus | null = null;
  return async (onChange) => {
    try {
      if (!status) {
        if (typeof navigator === 'undefined' || !navigator.permissions?.query) return 'unknown';
        status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
        status.addEventListener?.('change', () => onChange(status!.state as LocationPermission));
      }
      return status.state as LocationPermission;
    } catch {
      return 'unknown';
    }
  };
}

const RETRY_MS = 30_000;

/**
 * Keeps sharing in step with "which live mission should share right now" (setTarget).
 * Reconciles serially: overlapping calls (React StrictMode, fast navigation) coalesce and the
 * last target wins.
 */
export class AutoShare {
  private target: Id | null = null;
  /** Mission for which the phone/browser said no — never re-asked automatically. */
  private blockedFor: Id | null = null;
  /** The iOS permission prompt has been shown this session. */
  private askedNative = false;
  private lastAttempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private again = false;
  private readonly sharing: SharingControl;
  private readonly native: () => SightlineNative | null;
  private readonly queryPermission: NonNullable<AutoShareDeps['queryPermission']>;
  private readonly retryMs: number;

  constructor(deps: AutoShareDeps) {
    this.sharing = deps.sharing;
    this.native = deps.native ?? getNative;
    this.queryPermission = deps.queryPermission ?? browserPermissionQuery();
    this.retryMs = deps.retryMs ?? RETRY_MS;
    this.sharing.subscribe(() => this.onSharing());
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.schedule());
      onNative<{ location: NativePermission }>('permission', (d) => this.permissionChanged(d.location));
      // back from the Settings app: a blocked permission may have been turned on
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && this.blockedFor) void this.recheckBlocked();
      });
    }
  }

  /** The live mission that should share now, or null (setting off, not live, not crew, closed). */
  setTarget(missionId: Id | null): void {
    if (missionId === this.target) return;
    this.target = missionId;
    this.schedule();
  }

  /** The user asked (switched the setting on, tapped Try again): forget an earlier "no" once. */
  retry(): void {
    this.blockedFor = null;
    this.lastAttempt = 0;
    this.schedule();
  }

  /** The phone/browser permission changed (iOS 'permission' event, Permissions API change). */
  permissionChanged(p: LocationPermission): void {
    if (p === 'granted') {
      this.blockedFor = null;
      this.lastAttempt = 0;
    }
    this.schedule();
  }

  /** Wait until pending work is done (tests). */
  async settled(): Promise<void> {
    while (this.running) await new Promise((r) => setTimeout(r, 0));
  }

  private async recheckBlocked(): Promise<void> {
    const p = await this.permission();
    if (p === 'granted') this.permissionChanged('granted');
  }

  private onSharing(): void {
    const s = this.sharing.snapshot;
    if ((s.status === 'denied' || s.status === 'unsupported') && s.missionId) this.blockedFor = s.missionId;
    if (s.status === 'error' && s.missionId && s.missionId === this.target && !this.retryTimer) {
      this.retryTimer = setTimeout(() => {
        this.retryTimer = null;
        this.schedule();
      }, this.retryMs);
    }
  }

  private schedule(): void {
    if (this.running) {
      this.again = true;
      return;
    }
    this.running = true;
    queueMicrotask(() => void this.run());
  }

  private async run(): Promise<void> {
    try {
      do {
        this.again = false;
        await this.reconcile();
      } while (this.again);
    } catch {
      /* a failed start/stop is reflected in the sharing state */
    } finally {
      this.running = false;
    }
  }

  private async permission(): Promise<LocationPermission> {
    const n = this.native();
    if (n) {
      try {
        return n.locationPermission();
      } catch {
        return 'unknown';
      }
    }
    return this.queryPermission((p) => this.permissionChanged(p));
  }

  private async reconcile(): Promise<void> {
    const want = this.target;
    const s = this.sharing.snapshot;
    if (!want) {
      if (s.missionId || s.status !== 'off') await this.sharing.stop();
      return;
    }
    if (s.missionId === want && (s.status === 'active' || s.status === 'requesting')) return;
    if (this.blockedFor === want) {
      if (s.missionId !== want || (s.status !== 'denied' && s.status !== 'unsupported')) this.sharing.block(want);
      return;
    }
    if (s.missionId === want && s.status === 'error' && Date.now() - this.lastAttempt < this.retryMs) return; // the retry timer comes back
    const p = await this.permission();
    if (this.target !== want) {
      this.again = true;
      return;
    }
    if (p === 'denied') {
      this.blockedFor = want;
      this.sharing.block(want);
      return;
    }
    const native = this.native();
    if (native && p === 'prompt') {
      // one iOS prompt; the 'permission' event brings us back here
      if (!this.askedNative) {
        this.askedNative = true;
        native.requestLocationPermission();
      }
      return;
    }
    this.lastAttempt = Date.now();
    await this.sharing.start(want);
  }
}

export const autoShare = new AutoShare({ sharing: locationSharing });

// ───────────── the iOS app follows the mission with the page asleep ─────────────

/**
 * Tells the Airview Studio iOS app (build 7+, `sightlineNative.setMission`) which mission is open,
 * whether it is live and whether my location is shared — so it can speak callouts and keep sharing
 * with the phone locked. Calls only when something changed; a no-op in browsers and older builds.
 */
export class NativeMissionSync {
  private last: string | null = null;
  private readonly native: () => SightlineNative | null;

  constructor(native: () => SightlineNative | null = getNative) {
    this.native = native;
  }

  update(m: NativeMissionState): void {
    const n = this.native();
    if (!n?.setMission) return;
    const next: NativeMissionState = m.missionId ? { missionId: m.missionId, live: m.live, sharing: m.live && m.sharing } : { missionId: null, live: false, sharing: false };
    const key = `${next.missionId}|${next.live}|${next.sharing}`;
    if (key === this.last) return;
    this.last = key;
    try {
      n.setMission(next);
    } catch {
      this.last = null; // try again on the next change
    }
  }
}

export const nativeMissionSync = new NativeMissionSync();
